import { Injectable } from '@angular/core';
import type { PaddingOptions } from 'mapbox-gl';
import { MapBoxService } from '../mapbox.service';
import { BreakpointObserverService } from '../../../shared/services/breakpoint-observer.service';

declare const mapboxgl: typeof import('mapbox-gl');

/**
 * Where a popup nobody has dragged parks: over the search geocoder's own
 * corner. Only one of these is ever mounted - the magnifier button while the
 * search is closed, the geocoder field once it opens - and they sit a handful
 * of pixels apart, so the order is just for determinism. Measured rather than
 * hard-coded because that corner moves between the desktop and mobile layouts
 * (mapbox.component.scss, map-search.component.scss).
 */
const SEARCH_ANCHOR_SELECTORS = [
  '.search-open-button',
  '.mapboxgl-ctrl-top-left .mapboxgl-ctrl-geocoder',
];

/** Only reached before the search UI renders; matches its desktop inset. */
const FALLBACK_ANCHOR = { x: 24, y: 24 };

/** Room left between a fitted area and the viewport edge. */
const BOUNDS_PADDING = 40;

/** `.popup-container`'s fixed desktop width - see map-popup.component.scss. */
export const DESKTOP_POPUP_WIDTH = 620;

/**
 * Where the main map's popup sits on screen, and what that costs the map
 * underneath it.
 *
 * Split from `PopupService` because both it and `SearchGeocoderService` need
 * these answers, and those two already point at each other - the popup asks the
 * geocoder for an address, so the geocoder cannot inject the popup back.
 */
@Injectable({
  providedIn: 'root',
})
export class PopupPlacementService {
  /**
   * Where the user parked the popup, in map-container pixels. Sticky for the
   * session: once dragged, every popup after it opens at exactly that spot,
   * across closing and reopening. `null` until the first drag, and then the
   * card rests on the search geocoder instead.
   */
  private draggedPosition: { x: number; y: number } | null = null;

  constructor(
    private mapboxService: MapBoxService,
    private isMobileService: BreakpointObserverService
  ) {}

  /**
   * Parks the popup at its resting place: wherever the user last dragged it,
   * or, until they drag one, over the search geocoder. Deliberately independent
   * of the clicked cell - the card is a panel at a known spot, not a callout
   * chasing the map.
   */
  public place(popupEl: HTMLElement): void {
    const map = this.mapboxService.map;
    if (!map) return;

    const resting = this.getRestingPosition(map);
    const { x, y } = this.clamp(
      map,
      popupEl,
      resting.x,
      resting.y,
      this.draggedPosition !== null
    );

    this.write(map, popupEl, x, y);
  }

  /** Moves the card mid-drag and remembers where the user left it. */
  public dragTo(popupEl: HTMLElement, rawX: number, rawY: number): void {
    const map = this.mapboxService.map;
    if (!map) return;

    const { x, y } = this.clamp(map, popupEl, rawX, rawY, true);

    this.draggedPosition = { x, y };
    this.write(map, popupEl, x, y);
  }

  /**
   * Everything above works in map-container pixels, which is the space the
   * search anchor, the drag and the clamp are all expressed in. The card
   * itself hangs off the page host so it can outrank the mobile bottom bar
   * (`PopupService.portalOutOfMapLayout`), where it is positioned against the
   * viewport - so the container's own corner is added on the way out, and only
   * here.
   */
  private write(
    map: mapboxgl.Map,
    popupEl: HTMLElement,
    x: number,
    y: number
  ): void {
    const mapRect = map.getContainer().getBoundingClientRect();

    popupEl.style.left = `${mapRect.left + x}px`;
    popupEl.style.top = `${mapRect.top + y}px`;
  }

  /**
   * Pixels to shift a camera target by so the popup does not settle on top of
   * it: pass it as `flyTo`/`easeTo`'s `offset` when flying somewhere a popup is
   * about to describe. The target lands in the middle of whichever band of map
   * the card leaves free.
   *
   * `offset` rather than the transform's `padding`, which mapbox keeps: every
   * later camera move, `getCenter()` and the URL sync would inherit a skew that
   * is only true while a popup happens to be open.
   *
   * Only the popup is compensated for. The right menu is a flex sibling of the
   * map rather than an overlay (map.component.scss), so opening it shrinks the
   * canvas - the map's own centre is already the centre of what is left.
   */
  public getFocusOffset(map: mapboxgl.Map): [number, number] {
    // The card spans a phone's whole width, so there is no band to move into;
    // and the reports and comparison maps never open one at all.
    if (map !== this.mapboxService.map) return [0, 0];
    if (this.isMobileService.isMobile.getValue()) return [0, 0];

    const mapWidth = map.getContainer().offsetWidth;
    const left = this.getRestingPosition(map).x;
    const right = left + this.getPopupWidth();

    // Whichever side of the card keeps more map. Dragging it to the right edge
    // has to flip the target to the left, not push it under the card.
    const [start, end] =
      mapWidth - right >= left ? [right, mapWidth] : [0, left];

    return [(start + end) / 2 - mapWidth / 2, 0];
  }

  /**
   * Bounds padding that keeps a fitted area clear of the popup. The focus
   * offset is half the width of the strip the card leaves unusable, so
   * reserving twice it on that side centres the area in what remains.
   */
  public getBoundsPadding(map: mapboxgl.Map): PaddingOptions {
    const [dx] = this.getFocusOffset(map);

    return {
      top: BOUNDS_PADDING,
      bottom: BOUNDS_PADDING,
      left: BOUNDS_PADDING + Math.max(dx, 0) * 2,
      right: BOUNDS_PADDING + Math.max(-dx, 0) * 2,
    };
  }

  /**
   * Extra padding for the side of the map a phone's card covers. The card
   * spans the full width there, so nothing under it is visible at all. Zero
   * on desktop, where `getBoundsPadding` already moves the area beside it.
   */
  public getMobileCardPadding(map: mapboxgl.Map): {
    top: number;
    bottom: number;
  } {
    const popupEl = document.querySelector('.default-popup');
    if (!popupEl || !this.isMobileService.isMobile.getValue()) {
      return { top: 0, bottom: 0 };
    }

    const mapRect = map.getContainer().getBoundingClientRect();
    const cardRect = popupEl.getBoundingClientRect();
    const isUpperHalf =
      cardRect.top + cardRect.height / 2 < mapRect.top + mapRect.height / 2;

    return isUpperHalf
      ? { top: Math.max(0, cardRect.bottom - mapRect.top), bottom: 0 }
      : { top: 0, bottom: Math.max(0, mapRect.bottom - cardRect.top) };
  }

  /** Top-left corner the card rests at, before clamping. */
  private getRestingPosition(map: mapboxgl.Map): { x: number; y: number } {
    return this.draggedPosition ?? this.getSearchAnchor(map);
  }

  /** The search geocoder's top-left corner, in map-container pixels. */
  private getSearchAnchor(map: mapboxgl.Map): { x: number; y: number } {
    const mapEl = map.getContainer();
    // The geocoder is a control inside this map; the button that replaces it is
    // a sibling of the map in the page. Searching the container first is what
    // keeps a second map's control (reports, comparison) out of the answer.
    const searchEl = SEARCH_ANCHOR_SELECTORS.map(
      (selector) =>
        mapEl.querySelector(selector) ?? document.querySelector(selector)
    ).find((el): el is Element => !!el);

    if (!searchEl) return { ...FALLBACK_ANCHOR };

    const mapRect = mapEl.getBoundingClientRect();
    const searchRect = searchEl.getBoundingClientRect();

    return {
      x: searchRect.left - mapRect.left,
      y: searchRect.top - mapRect.top,
    };
  }

  /**
   * Measured while a card is up, because a hover popup is far narrower than a
   * cell one; the constant covers the common case of aiming the camera *before*
   * opening the popup that follows.
   */
  private getPopupWidth(): number {
    // Searched from the document, not the map: the card is lifted out of the
    // map (`PopupService.portalOutOfMapLayout`). Only one is ever mounted.
    const popupEl = document.querySelector(
      '.default-popup'
    ) as HTMLElement | null;

    return popupEl?.offsetWidth || DESKTOP_POPUP_WIDTH;
  }

  /**
   * @param isUserDrag true while dragging and for the position a drag left
   *   behind. The bottom menu is only avoided when *we* place the popup:
   *   someone dragging it has the whole card in front of them, so refusing to
   *   move past the auto-placed position just felt broken.
   */
  private clamp(
    map: mapboxgl.Map,
    popupEl: HTMLElement,
    rawX: number,
    rawY: number,
    isUserDrag = false
  ): { x: number; y: number } {
    const avoidChrome = this.isMobileService.isMobile.value && !isUserDrag;

    const mapRect = map.getContainer().getBoundingClientRect();

    const maxX = mapRect.width - popupEl.offsetWidth - 1;
    const minX = 1; // Make at least 1px away from navbar

    // Nothing is reserved at the top, on a phone included: the resting place is
    // the search field's own corner and the card is meant to cover it, exactly
    // as it does on a laptop. A floor there used to push the whole card ~33px
    // below the search button instead.
    //
    // The bottom is different - the mobile menu is the last ~75px of a map that
    // otherwise runs edge to edge. `.popup-container`'s matching height cap is
    // what keeps this range positive; the Math.max is what stops a popup that
    // is somehow still too tall from being pinned to a negative offset.
    const bottomChrome = avoidChrome ? this.readMobileBottomChrome(popupEl) : 0;

    const minY = 0;
    const maxY = Math.max(
      minY,
      mapRect.height - popupEl.offsetHeight - bottomChrome
    );

    let clampedX = rawX;
    let clampedY = rawY;

    if (clampedX < 0) clampedX = 0;
    if (clampedX > maxX) clampedX = maxX;
    if (clampedY < minY) clampedY = minY;
    if (clampedY > maxY) clampedY = maxY;
    if (clampedX < minX) clampedX = minX;

    return { x: clampedX, y: clampedY };
  }

  /**
   * The room the mobile bottom menu occupies, read from the card's own
   * `--popup-bottom-chrome`.
   *
   * The card already caps its `max-height` with that custom property, so
   * reading it here is what keeps the clamp and the cap describing one box.
   * Copying the number into TypeScript is what let the landscape override
   * (map-popup.component.scss) drift away from it: a landscape phone reserved
   * the portrait figures against a 390px viewport, the range collapsed, and
   * every popup was pinned to the same y with its foot under the bottom menu.
   */
  private readMobileBottomChrome(popupEl: HTMLElement): number {
    const containerEl = popupEl.querySelector('.popup-container');
    if (!containerEl) return 0;

    const styles = getComputedStyle(containerEl);

    return parsePx(styles.getPropertyValue('--popup-bottom-chrome'));
  }
}

/**
 * A CSS length in pixels, or 0 when the property is unset. Custom properties
 * come back as the raw token (`" 138px"`), which `parseFloat` handles.
 */
function parsePx(value: string): number {
  const parsed = parseFloat(value);
  return isFinite(parsed) ? parsed : 0;
}
